'use client';

/**
 * CategoryTreeCard — mode11's category-first browse tree, two levels.
 *
 * Level 1: the top-level category buckets with live counts from the
 * query-less `product_search` facet payload (sp-driven — the category
 * facet reads SemanticProfile). Level 2: expanding a bucket re-requests
 * with that bucket as the filter and paints the child facet's
 * drill-down counts (category → product_type: seating → chair, sofa,
 * stool). React Query caches each expansion's payload.
 *
 * Click semantics — single-select browse ("I'm looking at seating"):
 *   - bucket name  → filters[facet_key] = [bucket]; clears child filter
 *   - child name   → both filters (bucket + type)
 *   - chevron      → expand/collapse only, no filter change
 * The Filter lens keeps multi-select facet chips for refine.
 *
 * Visual idiom mirrors mode4's vendor_list tree (settled explorer
 * layout): surfaceBase.canvas em-base, text.heading header, bordered
 * bg-card rows, depthRamp names (0 root / 1 child), hover-revealed
 * counts, chevron gutter.
 *
 * BFF discipline: facet keys + fetch params come from the card spec's
 * `default_props` (facet_key / child_facet_key / browse_params) via
 * useCardProps — nothing hardcoded here. Strategy name resolves from
 * the capability registry. Regrouping tiers = backend edit.
 *
 * @see klyde_edge/ui_config/composition/cards/search.py — content_key="category_tree"
 * @see klyde_ipe/components/explorer/cards/vendors/list-card.tsx — the layout donor
 * @see feedback_cards-surface-agnostic-relocatable
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain explorer
 */

import { useMemo, useState } from 'react';
import { Loader2, Shapes, XCircle } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useProductSearchStore } from '@/lib/stores/product-search-store';
import { useProductLabel, useCardProps } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { Facet } from '@/components/canvas/cards/products/facet-card';
import {
  CategoryTreeFrame,
  CategoryAllRow,
  CategoryBucketRow,
  CategoryChildRow,
  CategoryChildLoading,
} from './category-tree-shell';

interface ProductSearchResponse {
  total: number;
  facets: Facet[];
}

export function CategoryTreeCard() {
  const filters = useProductSearchStore((s) => s.filters);
  const setFilter = useProductSearchStore((s) => s.setFilter);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const strategyName = useCapabilityStore((s) => s.strategyName('product_intelligence', 'search'));

  const labelTitle = useProductLabel('cat_tree_title');
  const labelAll = useProductLabel('cat_tree_all');
  const labelLoading = useProductLabel('cat_tree_loading');
  const labelError = useProductLabel('cat_tree_error');

  // BFF-declared params: which facets the two tree levels paint + the
  // query-less browse request. The root fetch is store-INDEPENDENT
  // (fixed params) so counts stay global while the grid re-scopes.
  const props = useCardProps('category_tree');
  const facetKey = (props.facet_key as string | undefined) ?? 'category';
  const childKey = (props.child_facet_key as string | undefined) ?? null;
  const browseParams = useMemo(
    () => (props.browse_params as object | undefined) ?? { query: '', filters: {}, limit: 1 },
    [props],
  );

  const { data, isLoading, error } = useStrategyData<ProductSearchResponse>(
    strategyName,
    browseParams,
    !!strategyName,
  );
  const res = data?.data;
  const facet = res?.facets?.find((f) => f.key === facetKey);
  const values = facet?.values ?? [];

  // Single-select browse state read back from the store.
  const selected = ((filters[facetKey] as string[] | undefined) ?? [])[0] ?? null;
  const selectedChild = childKey
    ? (((filters[childKey] as string[] | undefined) ?? [])[0] ?? null)
    : null;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (value: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });

  // Bucket click: scope to the bucket + swap the canvas to the results
  // grid (mode4's flip semantics — search-input-card.tsx does the same).
  // "All" / re-click clears the scope and flips back to the catalog.
  const pickBucket = (value: string | null) => {
    if (childKey) setFilter(childKey, null);
    const clearing = value === null || value === selected;
    setFilter(facetKey, clearing ? null : [value]);
    setExplorerSelection(clearing ? null : 'search');
  };
  // Child click: scope to bucket + child together.
  const pickChild = (bucket: string, child: string) => {
    setFilter(facetKey, [bucket]);
    if (childKey) setFilter(childKey, child === selectedChild && bucket === selected ? null : [child]);
    setExplorerSelection('search');
  };

  useCardExport(
    useMemo(() => ({
      label: labelTitle || 'Categories',
      content_key: 'category_tree',
      status: (!res && isLoading ? 'loading' : error ? 'error' : res ? 'loaded' : 'loading') as
        'loading' | 'loaded' | 'error',
      query_key: strategyName ? ['strategy-data', strategyName, browseParams] : null,
      text: selected
        ? `${facetKey}: ${selected}${selectedChild ? ` › ${selectedChild}` : ''}`
        : `${values.length} ${facetKey} buckets`,
      json: res ? { facet: facetKey, selected, selected_child: selectedChild, buckets: values.map((v) => `${v.value}:${v.count}`) } : null,
    }), [labelTitle, res, isLoading, error, strategyName, browseParams, facetKey, selected, selectedChild, values]),
  );

  if (isLoading && !res) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'flex items-center gap-2 px-3 py-2')}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {labelLoading}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.body, 'px-3 py-2 flex items-center gap-2 text-destructive')}>
        <XCircle className="h-3.5 w-3.5" />
        {labelError}
      </div>
    );
  }

  return (
    <CategoryTreeFrame title={labelTitle} icon={Shapes}>
      {/* No count on the All row: the payload's `total` is the capped result
          count, not the catalogue size. */}
      <CategoryAllRow label={labelAll} active={selected === null} onClick={() => pickBucket(null)} />
      {values.map((v) => (
        <BucketRow
          key={v.value}
          bucket={v.value}
          label={v.label ?? v.value}
          count={v.count}
          isActive={v.value === selected}
          activeChild={v.value === selected ? selectedChild : null}
          expanded={expanded.has(v.value)}
          expandable={!!childKey}
          onToggleExpand={() => toggleExpand(v.value)}
          onPick={() => pickBucket(v.value)}
          onPickChild={(child) => pickChild(v.value, child)}
          strategyName={strategyName}
          browseParams={browseParams}
          facetKey={facetKey}
          childKey={childKey}
        />
      ))}
    </CategoryTreeFrame>
  );
}

/** One bucket row + its lazily-fetched children. A component (not an
 *  inline map) so the drill-down hook runs unconditionally per row —
 *  enabled only while expanded; React Query caches each payload. */
function BucketRow({
  bucket, label, count, isActive, activeChild, expanded, expandable,
  onToggleExpand, onPick, onPickChild,
  strategyName, browseParams, facetKey, childKey,
}: {
  bucket: string;
  label: string;
  count: number;
  isActive: boolean;
  activeChild: string | null;
  expanded: boolean;
  expandable: boolean;
  onToggleExpand: () => void;
  onPick: () => void;
  onPickChild: (child: string) => void;
  strategyName: string | null;
  browseParams: object;
  facetKey: string;
  childKey: string | null;
}) {
  // Drill-down: same browse request scoped to this bucket; the child
  // facet's counts come back within-bucket. Fetch only while expanded.
  const drillParams = useMemo(
    () => ({ ...(browseParams as Record<string, unknown>), filters: { [facetKey]: [bucket] } }),
    [browseParams, facetKey, bucket],
  );
  const { data, isLoading } = useStrategyData<ProductSearchResponse>(
    strategyName,
    drillParams,
    !!strategyName && expanded && !!childKey,
  );
  const children = childKey
    ? (data?.data?.facets?.find((f) => f.key === childKey)?.values ?? [])
    : [];

  return (
    <CategoryBucketRow
      label={label}
      count={count}
      active={isActive}
      expandable={expandable}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      onPick={onPick}
    >
      {isLoading && children.length === 0 && <CategoryChildLoading />}
      {children.map((c) => {
        const childActive = isActive && c.value === activeChild;
        return (
          <CategoryChildRow
            key={c.value}
            label={c.label ?? c.value}
            active={childActive}
            onClick={() => onPickChild(c.value)}
            trailing={
              <span className={cn(text.muted, 'shrink-0 opacity-0 group-hover/leaf:opacity-100 transition-opacity')}>
                {c.count}p
              </span>
            }
          />
        );
      })}
    </CategoryBucketRow>
  );
}
